import { throttle } from 'lodash-es';
import { useLayoutEffect, useState } from 'react';

import { SIXTY_FPS } from '../constants.js';

/**
 * Provides the scroll state of the referenced element.
 */
export const useScroll = (
  content: HTMLElement | null | undefined,
  horizontal = false,
): ReturnType<typeof calcScrollState> => {
  const [scroll, setScroll] = useState<ReturnType<typeof useScroll>>(
    calcScrollState(content, horizontal),
  );

  useLayoutEffect(() => {
    const handleScroll = throttle(
      () => setScroll(calcScrollState(content, horizontal)),
      SIXTY_FPS,
    );

    content?.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    setScroll(calcScrollState(content, horizontal));

    return () => content?.removeEventListener('scroll', handleScroll);
  }, [content, content?.scrollHeight, content?.scrollWidth, horizontal]);

  return scroll;
};

/**
 * Calculates the scroll state of the referenced element.
 */
export function calcScrollState(
  container: HTMLElement | null | undefined,
  horizontal: boolean,
): {
  start: number;
  end: number;
  max: number;
  scrollable: boolean;
} {
  if (!container) {
    return {
      start: 0,
      end: 0,
      max: 0,
      scrollable: false,
    };
  }

  return horizontal
    ? {
        start: container.scrollLeft,
        end: container.scrollLeft + container.clientWidth,
        max: container.scrollWidth,
        scrollable: container.scrollWidth > container.clientWidth,
      }
    : {
        start: container.scrollTop,
        end: container.scrollTop + container.clientHeight,
        max: container.scrollHeight,
        scrollable: container.scrollHeight > container.clientHeight,
      };
}
